import React, { Fragment, Ref } from 'react'
import { css, cx } from '@emotion/css'
import { theme as deprecatedTheme } from '../../styled'
import {
  IconAdd,
  theme
} from '@contrast-security-inc/design-system-foundations'
import { FormTextProps, FormTextContainer } from '../form-text'

const hiddenFileInputStyle = css`
  height: 0;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  width: 0;
`

export const labelBoxStyle = css`
  font-family: ${deprecatedTheme.fontFamilies.secondary};
  font-weight: ${deprecatedTheme.fontWeights.secondary.regular};
  font-size: 14px;
  color: ${theme.color.neutral50};
  border: 1px solid ${theme.color.transparent};
  background-color: ${theme.color.neutral20};
  display: flex;
  width: 100%;
  min-height: 34px;
  height: 34px;
  outline: 0;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  border-radius: 3px 4px 4px 3px;
  padding: 0 0 0 8px;

  &:focus {
    border: 1px solid ${theme.color.turquoise70};
    box-shadow: 0 0 1px ${theme.color.turquoise70};
  }

  &::placeholder {
    opacity: 0.3;
  }
`

const errorColor = theme.color.red50

export const labelBoxInvalidStyle = cx(
  labelBoxStyle,
  css`
    color: ${errorColor};
    border: 1px solid ${errorColor};

    &:focus {
      border: 1px solid ${errorColor};
      box-shadow: 0 0 1px ${errorColor};
    }
  `
)

export const layoutCSS = css`
  width: 100%;
`

export const buildLabelClassName = (
  renderLayout?: () => React.ReactNode,
  error?
) => {
  if (!!renderLayout) {
    return layoutCSS
  }

  if (!!error) {
    return labelBoxInvalidStyle
  }

  return labelBoxStyle
}

export const buildDisplayValue = (
  placeholder?: string,
  displayValue?: string,
  file?: File
) => {
  if (!!displayValue) {
    return displayValue
  }

  if (!!file) {
    return file.name
  }

  return placeholder
}

export interface FileInputProps extends FormTextProps {
  /**
   * Will be passed as the ref to the actual input element
   */
  readonly inputRef?: Ref<HTMLInputElement>

  readonly accept?: string

  readonly onChange?: (FileList) => void

  readonly displayValue?: string

  readonly file?: File

  readonly renderLayout?: () => React.ReactNode

  readonly multiple?: boolean
}

export function FileInput({
  onBlur,
  inputRef,
  displayValue,
  file,
  renderLayout,
  multiple,
  ...props
}: FileInputProps) {
  return (
    <FormTextContainer {...props}>
      {({
        disabled,
        errorMessage,
        id,
        label,
        onChange,
        placeholder,
        renderLabel,
        tabIndex,
        ...props
      }) => {
        const error = !!errorMessage

        const labelClassName = buildLabelClassName(renderLayout, error)

        return (
          <React.Fragment>
            <label htmlFor={id} className={labelClassName}>
              {!!renderLayout ? (
                renderLayout()
              ) : (
                <Fragment>
                  <span
                    className={css`
                      flex: 1 1 auto;
                      align-self: center;
                      padding: 0 0 2px 0;
                    `}
                  >
                    {buildDisplayValue(placeholder, displayValue, file)}
                  </span>
                  <div
                    className={css`
                      border-color: ${
                        error ? errorColor : theme.color.neutral40
                      };
                      border-radius: 0 3px 3px 0;
                      padding: 9px 8px 7px 10px;
                      background-color: ${theme.color.white};

                      &:hover {
                        background-color: ${theme.color.neutral40};
                        border-color: ${error ? errorColor : 'inherit'};
                    `}
                  >
                    <IconAdd size="sm" />
                  </div>
                </Fragment>
              )}
            </label>
            <input
              id={id}
              aria-hidden={true}
              aria-label={renderLabel || !id ? label : undefined}
              aria-invalid={error ? true : undefined}
              disabled={disabled}
              className={hiddenFileInputStyle}
              onBlur={onBlur}
              onChange={
                onChange
                  ? (e) => onChange(!!e.target.files ? e.target.files : [])
                  : undefined
              }
              ref={inputRef}
              type="file"
              tabIndex={tabIndex}
              multiple={!!multiple}
              data-testid="file-input"
              {...props}
            />
          </React.Fragment>
        )
      }}
    </FormTextContainer>
  )
}
